import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";

const InvoicePopup = ({ openInvoicePopup, invoice, handleClose, status }) => {
  const handleDownload = (orderId) => {
    axios
      .get(`https://sakhisshop.com/api/invoice/download/${orderId}`, {
        responseType: "arraybuffer",
      })
      .then((response) => {
        const file = new Blob([response.data], { type: "application/pdf" });
        const fileURL = URL.createObjectURL(file);
        const link = document.createElement("a");
        link.href = fileURL;
        link.download = `invoice_${orderId}.pdf`;
        link.click();
      })
      .catch((error) => {
        console.log("Error downloading invoice:", error);
      });
  };

  // ✅ Calculate grand total safely
  const grandTotal =
    invoice?.items?.reduce(
      (sum, item) => sum + (Number(item.amount) || 0),
      0,
    ) || 0;

  return (
    <Dialog
      open={openInvoicePopup}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: { width: "100%", m: { xs: 1, sm: 2 } },
      }}
    >
      <Box sx={{ position: "relative" }}>
        {status === "Cancelled" && (
          <Typography
            sx={{
              position: "absolute",
              top: "40%",
              left: "50%",
              transform: "translate(-50%, -50%) rotate(-20deg)",
              color: "red",
              fontSize: { xs: 60, sm: 100 },
              fontWeight: "bold",
              opacity: 0.1,
              zIndex: 1,
              pointerEvents: "none",
              userSelect: "none",
            }}
          >
            CANCELLED
          </Typography>
        )}

        {/* Header */}
        <Box sx={{ position: "relative", zIndex: 20 }}>
          <Box
            sx={{
              textAlign: "center",
              py: 2,
              backgroundColor: "#f1f1f1",
              mt: 1,
              fontWeight: "bold",
              color: "#2e7d32",
            }}
          >
            <Typography variant="h6" sx={{ mt: 1 }}>
              🛒 {invoice?.shopName || "Shop Name"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              📍 {invoice?.address || "Shop Address"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              📞 {invoice?.contactNumber || "Contact Number"}
            </Typography>
          </Box>
        </Box>

        <DialogTitle sx={{ backgroundColor: "#1976d2", color: "#fff" }}>
          Invoice - {invoice?.orderId}
        </DialogTitle>

        <DialogContent
          dividers
          sx={{ backgroundColor: "#f9f9f9", overflowX: "hidden" }}
        >
          {/* Invoice Summary */}
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            gap={2}
            mb={2}
          >
            <Box>
              <Typography variant="h6" gutterBottom>
                Billed To:
              </Typography>
              <Typography variant="body1">{invoice?.customerName}</Typography>
              <Typography variant="body2">
                Order Date: {invoice?.orderDate || "Not Available"}
              </Typography>
            </Box>
            <Box textAlign={{ xs: "left", sm: "right" }}>
              <Typography variant="h6" gutterBottom>
                Invoice Summary
              </Typography>
              <Typography variant="body1">
                Order ID: <strong>{invoice?.orderId}</strong>
              </Typography>
              <Typography variant="body1">
                Payment Status:{" "}
                <strong
                  style={{
                    color:
                      invoice?.paymentStatus === "PAID" ? "green" : "orange",
                  }}
                >
                  {invoice?.paymentStatus}
                </strong>
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                <strong>Total: ₹{grandTotal.toFixed(2)}</strong>
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Items Table */}
          <Box sx={{ overflowX: "auto" }}>
            <Paper elevation={2}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: "#e3f2fd" }}>
                  <TableRow>
                    <TableCell>
                      <strong>#</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Item Description</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Price (₹)</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Qty</strong>
                    </TableCell>
                    <TableCell>
                      <strong>UOM</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Total (₹)</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoice?.items?.map((item, index) => {
                    const price = Number(
                      item.price || item.customPrice || item.mrp || 0,
                    );
                    const amount = Number(item.amount || item.quantity * price);
                    return (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{item.productName}</TableCell>
                        <TableCell>₹{price.toFixed(2)}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>
                          {{
                            G: "Gram",
                            KG: "Kilogram",
                            PCS: "Pieces",
                            L: "Litre",
                          }[item.uom?.toUpperCase()] || item.uom}
                        </TableCell>
                        <TableCell>₹{amount.toFixed(2)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Grand Total */}
          <Typography variant="h6" align="right">
            Grand Total: <strong>₹{grandTotal.toFixed(2)}</strong>
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={() => handleDownload(invoice?.orderId)}
          >
            Download PDF
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<CloseIcon />}
            onClick={handleClose}
          >
            Close
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default InvoicePopup;
